import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { UserCheck, Search, Download, Calendar, Mail, Phone, User, Building2, Globe, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

interface ProfessionalApplication {
  id: string;
  profession: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  license_number: string;
  license_state: string;
  experience_years: string;
  specialties: string[];
  consultation_fee: string;
  company_name: string;
  company_website: string;
  service_areas: string[];
  availability: string;
  certifications: string;
  terms_agreed: boolean;
  status: string;
  created_at: string;
}

export default function AdminFormsProfessional() {
  const { isAdmin, isLoading: authLoading, user } = useAdminAuth();
  const [applications, setApplications] = useState<ProfessionalApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [professionFilter, setProfessionFilter] = useState('all');
  const [retryCount, setRetryCount] = useState(0);
  const [selectedApplication, setSelectedApplication] = useState<ProfessionalApplication | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!authLoading && isAdmin && user) {
      fetchApplications();
    }
  }, [authLoading, isAdmin, user]);

  // Support drill-down via query params like ?profession=&q=
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const profession = params.get('profession');
      const q = params.get('q');
      if (profession) setProfessionFilter(profession);
      if (q) setSearchTerm(q);
    } catch {}
  }, []);


  const fetchApplications = async () => {
    if (!isAdmin || !user) {
      console.log('Not authenticated as admin, skipping fetch');
      setLoading(false);
      return;
    }

    try {
      console.log('Fetching professional applications with auth:', { userId: user.id, isAdmin });
      
      const { data, error } = await supabase.rpc('list_professional_applications_admin');

      if (error) {
        console.error('Supabase error:', error);
        
        // Retry logic for auth context issues
        if (error.code === 'PGRST301' && retryCount < 3) {
          console.log(`Retrying fetch (attempt ${retryCount + 1})`);
          setRetryCount(prev => prev + 1);
          setTimeout(() => fetchApplications(), 1000 * (retryCount + 1));
          return;
        }
        
        throw error;
      }
      
      // The RPC now returns the array directly
      const result = data as any;
      console.log('Raw result from database:', result);
      console.log('Full data response:', data);
      
      let processedApplications = [];
      if (Array.isArray(result)) {
        processedApplications = result;
      } else {
        processedApplications = [];
      }
      
      // Filter out null entries and ensure data integrity
      processedApplications = processedApplications.filter(app => app && app.id).map(app => ({
        ...app,
        status: app.status || 'pending',
        profession: app.profession || 'Unknown'
      }));
      
      console.log('Successfully fetched applications:', processedApplications.length);
      console.log('Processed applications:', processedApplications);
      setApplications(processedApplications);
      setRetryCount(0);
    } catch (error) {
      console.error('Error fetching professional applications:', error);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  // Map profession codes to filter values
  const getProfessionFilterValue = (profession: string) => {
    const professionMap: { [key: string]: string } = {
      'OT': 'occupational_therapist',
      'Occupational Therapist': 'occupational_therapist',
      'CAPS': 'caps_specialist',
      'Physical Therapist': 'physical_therapist',
      'PT': 'physical_therapist',
      'Home Safety Specialist': 'home_safety_specialist',
      'Geriatric Care Manager': 'geriatric_care_manager',
      'Senior Care Professional': 'senior_care_professional',
      'Independent Caregiver': 'independent_caregiver'
    };
    return professionMap[profession] || profession.toLowerCase().replace(/\s+/g, '_');
  };

  const filteredApplications = applications.filter(application => {
    const matchesSearch = 
      application.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      application.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      application.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      application.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const applicationProfessionFilter = getProfessionFilterValue(application.profession || '');
    const matchesProfession = professionFilter === 'all' || applicationProfessionFilter === professionFilter;
    
    return matchesSearch && matchesProfession;
  });

  const getStatusBadge = (status: string) => {
    // Handle undefined or null status
    const safeStatus = status || 'pending';
    
    const variants = {
      'pending': 'destructive',
      'under_review': 'default', 
      'approved': 'default',
      'rejected': 'secondary'
    } as const;
    
    const displayStatus = safeStatus.charAt(0).toUpperCase() + safeStatus.slice(1).replace('_', ' ');
    return <Badge variant={variants[safeStatus as keyof typeof variants] || 'secondary'}>{displayStatus}</Badge>;
  };

  const getProfessionName = (profession: string) => {
    const professionMap: { [key: string]: string } = {
      'OT': 'Occupational Therapist',
      'CAPS': 'CAPS Specialist',
      'PT': 'Physical Therapist',
      'Senior Care Professional': 'Senior Care Professional',
      'Independent Caregiver': 'Independent Caregiver'
    };
    return professionMap[profession] || profession;
  };

  const handleViewApplication = (application: ProfessionalApplication) => {
    setSelectedApplication(application);
    setIsViewModalOpen(true);
  };

  const approveApplication = async (applicationId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('review-professional-application', {
        body: { applicationId, action: 'approve' },
      });
      if (error || !data?.success) throw new Error(data?.message || error?.message);

      // Optimistically update state
      setApplications((prev) => prev.map((a) => a.id === applicationId ? { ...a, status: 'approved' } : a));
      if (selectedApplication?.id === applicationId) {
        setSelectedApplication({ ...selectedApplication, status: 'approved' });
      }

      const isAffiliate = data?.role_title === 'Affiliate Partner';
      toast({
        title: 'Application approved',
        description: isAffiliate
          ? 'Affiliate referral source created and magic login link sent to their email.'
          : 'The professional will now appear in the directory.',
      });
    } catch (e: any) {
      console.error('Approve failed:', e);
      toast({ title: 'Approval failed', description: e.message || 'Please try again.', variant: 'destructive' as any });
    }
  };

  const rejectApplication = async (applicationId: string) => {
    try {
      const reason = window.prompt('Optional: Provide a rejection reason');
      const { data, error } = await supabase.functions.invoke('review-professional-application', {
        body: { applicationId, action: 'reject', rejectionReason: reason ?? null },
      });
      if (error || !data?.success) throw new Error(data?.message || error?.message);

      setApplications((prev) => prev.map((a) => a.id === applicationId ? { ...a, status: 'rejected' } : a));
      if (selectedApplication?.id === applicationId) {
        setSelectedApplication({ ...selectedApplication, status: 'rejected' });
      }
      toast({ title: 'Application rejected', description: 'The applicant has been marked as rejected.' });
    } catch (e: any) {
      console.error('Reject failed:', e);
      toast({ title: 'Rejection failed', description: e.message || 'Please try again.', variant: 'destructive' as any });
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Professional Applications</h1>
          <p className="text-muted-foreground">
            Manage professional service provider applications
          </p>
        </div>
        <Button>
          <Download className="mr-2 h-4 w-4" />
          Export
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Applications</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{applications.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {applications.filter(a => new Date(a.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Review</CardTitle>
            <Badge variant="destructive" className="h-4 w-4 p-0"></Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {applications.filter(a => (a.status || 'pending') === 'pending').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {applications.filter(a => (a.status || 'pending') === 'approved').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Applications</CardTitle>
          <CardDescription>Search and filter professional applications</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <Select value={professionFilter} onValueChange={setProfessionFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Profession" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Professions</SelectItem>
                <SelectItem value="occupational_therapist">Occupational Therapist</SelectItem>
                <SelectItem value="caps_specialist">CAPS Specialist</SelectItem>
                <SelectItem value="home_safety_specialist">Home Safety Specialist</SelectItem>
                <SelectItem value="geriatric_care_manager">Geriatric Care Manager</SelectItem>
                <SelectItem value="senior_care_professional">Senior Care Professional</SelectItem>
                <SelectItem value="independent_caregiver">Independent Caregiver</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Applications Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Applications ({filteredApplications.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Professional</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>License & Experience</TableHead>
                <TableHead>Experience</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredApplications.map((application) => (
                <TableRow key={application.id}>
                  <TableCell className="font-medium">
                    <div className="space-y-1">
                      <div className="flex items-center">
                        <User className="mr-2 h-4 w-4" />
                        {application.first_name} {application.last_name}
                      </div>
                      <div className="text-sm text-muted-foreground">{application.profession}</div>
                      {application.company_name && (
                        <div className="text-sm text-muted-foreground">{application.company_name}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center text-sm">
                        <Mail className="mr-1 h-3 w-3" />
                        {application.email}
                      </div>
                      {application.phone && (
                        <div className="flex items-center text-sm text-muted-foreground">
                          <Phone className="mr-1 h-3 w-3" />
                          {application.phone}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-sm font-medium">
                        {application.license_number} ({application.license_state})
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {application.experience_years} experience
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {application.specialties?.slice(0, 2).map((specialty, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {specialty}
                        </Badge>
                      ))}
                      {application.specialties?.length > 2 && (
                        <Badge variant="outline" className="text-xs">
                          +{application.specialties.length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(application.status || 'pending')}</TableCell>
                  <TableCell>
                    {format(new Date(application.created_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewApplication(application)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredApplications.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No applications found matching your criteria
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Application Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Professional Application Details</DialogTitle>
            <DialogDescription>
              Complete information for this professional application
            </DialogDescription>
          </DialogHeader>
          
          {selectedApplication && (
            <div className="flex-1 overflow-y-auto space-y-6 pr-2">
              {/* Personal Information */}
              <div className="grid gap-4">
                <h3 className="text-lg font-semibold border-b pb-2">Personal Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">First Name</label>
                    <p className="font-medium">{selectedApplication.first_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Last Name</label>
                    <p className="font-medium">{selectedApplication.last_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Profession</label>
                    <Badge variant="outline">{getProfessionName(selectedApplication.profession)}</Badge>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="font-medium">{selectedApplication.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Phone</label>
                    <p className="font-medium">{selectedApplication.phone}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Experience</label>
                    <p className="font-medium">{selectedApplication.experience_years} years</p>
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div className="grid gap-4">
                <h3 className="text-lg font-semibold border-b pb-2">Professional Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">License Number</label>
                    <p className="font-medium">{selectedApplication.license_number}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">License State</label>
                    <p className="font-medium">{selectedApplication.license_state}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Consultation Fee</label>
                    <p className="font-medium">{selectedApplication.consultation_fee || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Availability</label>
                    <p className="font-medium">{selectedApplication.availability}</p>
                  </div>
                </div>
                
                <div className="grid gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Specialties</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedApplication.specialties.map((specialty, index) => (
                        <Badge key={index} variant="secondary">{specialty}</Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Service Areas</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedApplication.service_areas.map((area, index) => (
                        <Badge key={index} variant="outline">{area}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
                
                {selectedApplication.certifications && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Additional Certifications</label>
                    <div className="bg-muted p-3 rounded-md mt-1">
                      <p className="whitespace-pre-wrap">{selectedApplication.certifications}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Company Information */}
              {selectedApplication.company_name && (
                <div className="grid gap-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Company Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Company Name</label>
                      <p className="font-medium">{selectedApplication.company_name}</p>
                    </div>
                    {selectedApplication.company_website && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Website</label>
                        <p className="font-medium">
                          <a 
                            href={selectedApplication.company_website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {selectedApplication.company_website}
                          </a>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Administrative Information */}
              <div className="grid gap-4">
                <h3 className="text-lg font-semibold border-b pb-2">Administrative Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    {getStatusBadge(selectedApplication.status)}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Terms Agreed</label>
                    <Badge variant={selectedApplication.terms_agreed ? "default" : "destructive"}>
                      {selectedApplication.terms_agreed ? "Yes" : "No"}
                    </Badge>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Applied On</label>
                    <p className="font-medium">{format(new Date(selectedApplication.created_at), 'PPP pp')}</p>
                  </div>
                </div>
              </div>

              {/* Actions - Fixed at bottom */}
              <div className="flex flex-wrap items-center justify-end gap-2 pt-4 border-t bg-background sticky bottom-0">
                <Button variant="outline" onClick={() => setIsViewModalOpen(false)}>
                  Close
                </Button>
                <Button variant="secondary" onClick={() => window.open(`tel:${selectedApplication.phone || ''}`)}>
                  <Phone className="h-4 w-4 mr-2" />
                  Call
                </Button>
                <Button onClick={() => window.open(`mailto:${selectedApplication.email}?subject=SeniorThrive - Professional Application Follow-up`)}>
                  <Mail className="h-4 w-4 mr-2" />
                  Contact
                </Button>
                {selectedApplication.status !== 'approved' && (
                  <Button className="bg-primary text-white" onClick={() => approveApplication(selectedApplication.id)}>
                    Approve
                  </Button>
                )}
                {selectedApplication.status !== 'rejected' && (
                  <Button variant="destructive" onClick={() => rejectApplication(selectedApplication.id)}>
                    Reject
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}